import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Heading,
  Input,
  Spinner,
  Table,
  Tag,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import SortHeader from "../components/SortHeader";
import { useSearchParams } from "react-router-dom";
import {
  AuditsApi,
  type AuditDiff,
  type AuditListResponse,
  type AuditSortField,
  type SortDir,
} from "../api/audit";
import Pagination from "../components/Pagination";
import logger from "../analytics/logger";
import { fmtDate } from "../utils/datetime";
import { readSort, writeSort, SORT_KEYS } from "../lib/tableSort";

type Form = {
  created_after: string;
  created_before: string;
  q: string;
};

const EMPTY_FORM: Form = { created_after: "", created_before: "", q: "" };

function formFromParams(p: URLSearchParams): Form {
  return {
    created_after: p.get("created_after") ?? "",
    created_before: p.get("created_before") ?? "",
    q: p.get("q") ?? "",
  };
}

const ENTITY_LABELS: Record<string, string> = {
  creator: "Creator",
  organization: "Organization",
  work: "Work",
  certification: "Certification",
  email_template: "Email template",
};

function entityLabel(t: string): string {
  return ENTITY_LABELS[t] ?? (t ? t.charAt(0).toUpperCase() + t.slice(1) : "—");
}

// The merged Entity column shows the creator's email for creator rows and the
// raw entity UUID for work/certification rows.
function entityRef(a: { entity_type: string; entity_id: string; creator_email: string | null }): string {
  if (a.entity_type === "creator") return a.creator_email ?? a.entity_id;
  return a.entity_id;
}

function diffText(diff: AuditDiff): string {
  if (diff == null) return "—";
  try {
    return JSON.stringify(diff);
  } catch {
    return String(diff);
  }
}

const PREFS_KEY = "support_v2_audit_table_v1";
const COL_MIN = 60;
const COL_MAX = 600;
const DEFAULT_WIDTHS: Record<AuditSortField, number> = {
  created_at: 170,
  actor: 200,
  entity_type: 320,
  action: 180,
};
// Non-sortable columns rendered with fixed widths.
const REASON_WIDTH = 240;
const DIFF_WIDTH = 360;

type TablePrefs = {
  widths: Partial<Record<AuditSortField, number>>;
  sort?: AuditSortField;
  dir?: SortDir;
};

function loadPrefs(): TablePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { widths: {} };
    const parsed = JSON.parse(raw);
    return { widths: parsed.widths ?? {}, sort: parsed.sort, dir: parsed.dir };
  } catch {
    return { widths: {} };
  }
}

function savePrefs(p: TablePrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // non-fatal
  }
}

export default function Audit() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);
  const pageSize = Math.max(1, parseInt(params.get("page_size") ?? "40", 10) || 40);

  const [draft, setDraft] = useState<Form>(() => formFromParams(params));
  const [data, setData] = useState<AuditListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await AuditsApi.list({
          page,
          page_size: pageSize,
          q: params.get("q") ?? undefined,
          created_after: params.get("created_after") ?? undefined,
          created_before: params.get("created_before") ?? undefined,
          sort: (params.get("sort") as AuditSortField | null) ?? undefined,
          dir: (params.get("dir") as SortDir | null) ?? undefined,
        });
        if (active) setData(d);
      } catch (e) {
        if (!active) return;
        logger.error("audit_list_failed", e);
        setError(
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            "Failed to load audit events",
        );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [params, page, pageSize]);

  const refresh = () => {
    const next = new URLSearchParams();
    if (draft.created_after) next.set("created_after", draft.created_after);
    if (draft.created_before) next.set("created_before", draft.created_before);
    if (draft.q) next.set("q", draft.q);
    next.set("page", "1");
    setParams(next);
  };

  const reset = () => {
    setDraft(EMPTY_FORM);
    setParams(new URLSearchParams({ page: "1" }));
  };

  const setPageSize = (size: number) => {
    const next = new URLSearchParams(params);
    next.set("page_size", String(size));
    next.set("page", "1");
    setParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  const [prefs, setPrefs] = useState<TablePrefs>(() => loadPrefs());

  // On mount only: if URL has no sort/dir, seed it from the remembered
  // sessionStorage sort (defaults to created_at desc if nothing remembered).
  useEffect(() => {
    if (!params.get("sort")) {
      const remembered = readSort(SORT_KEYS.audit);
      const next = new URLSearchParams(params);
      next.set("sort", remembered.sort);
      next.set("dir", remembered.dir);
      setParams(next, { replace: true });
    }
    // Intentionally mount-only: re-seeding on every params change would fight
    // the user's own sort clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remembered = readSort(SORT_KEYS.audit);
  const sort = (params.get("sort") as AuditSortField | null) ?? (remembered.sort as AuditSortField);
  const dir = (params.get("dir") as SortDir | null) ?? remembered.dir;

  // Persist the active sort to sessionStorage (source of truth on next mount).
  // We no longer mirror sort/dir into React state here (nothing reads them, and
  // set-state-in-effect flags it); column widths still live in prefs.
  useEffect(() => {
    writeSort(SORT_KEYS.audit, { sort, dir });
  }, [sort, dir]);

  const widthOf = (f: AuditSortField) => prefs.widths[f] ?? DEFAULT_WIDTHS[f];

  const setWidth = useCallback((f: AuditSortField, w: number) => {
    setPrefs((p) => {
      const next: TablePrefs = {
        ...p,
        widths: { ...p.widths, [f]: Math.max(COL_MIN, Math.min(COL_MAX, Math.round(w))) },
      };
      savePrefs(next);
      return next;
    });
  }, []);

  const toggleSort = (field: AuditSortField) => {
    const next = new URLSearchParams(params);
    if (sort === field) {
      next.set("dir", dir === "asc" ? "desc" : "asc");
    } else {
      next.set("sort", field);
      next.set("dir", field === "created_at" ? "desc" : "asc");
    }
    next.set("page", "1");
    setParams(next);
  };

  const sortHeaderProps = (field: AuditSortField) => ({
    active: sort === field,
    dir,
    onToggle: () => toggleSort(field),
    width: widthOf(field),
    onResize: (w: number) => setWidth(field, w),
    onResetWidth: () => setWidth(field, DEFAULT_WIDTHS[field]),
  });

  return (
    <Box p={6}>
      <HStack justify="space-between" align="center" mb={4}>
        <Heading size="lg">Audit</Heading>
      </HStack>

      <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
        <HStack spacing={3} wrap="wrap">
          <HStack spacing={1}>
            <Input
              size="sm"
              type="date"
              value={draft.created_after}
              onChange={(e) => setDraft({ ...draft, created_after: e.target.value })}
              maxW="160px"
              title="On or after"
              aria-label="On or after"
            />
            <Box as="span" color="gray.500" fontSize="sm">
              –
            </Box>
            <Input
              size="sm"
              type="date"
              value={draft.created_before}
              onChange={(e) => setDraft({ ...draft, created_before: e.target.value })}
              maxW="160px"
              title="On or before"
              aria-label="On or before"
            />
          </HStack>
          <Input
            size="sm"
            flex="1"
            minW="280px"
            placeholder="Search by entity UUID or creator email…"
            value={draft.q}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") refresh();
            }}
          />
          <Button size="sm" colorScheme="blue" onClick={refresh}>
            Refresh
          </Button>
          <Button size="sm" variant="ghost" onClick={reset}>
            Reset
          </Button>
        </HStack>
      </Box>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {loading ? (
        <Box py={10} textAlign="center">
          <Spinner />
        </Box>
      ) : data ? (
        <>
          <Box overflowX="auto">
            <Table
              size="sm"
              variant="simple"
              sx={{ tableLayout: "fixed", width: "max-content" }}
            >
              <colgroup>
                <col style={{ width: `${widthOf("created_at")}px` }} />
                <col style={{ width: `${widthOf("actor")}px` }} />
                <col style={{ width: `${widthOf("entity_type")}px` }} />
                <col style={{ width: `${widthOf("action")}px` }} />
                <col style={{ width: `${REASON_WIDTH}px` }} />
                <col style={{ width: `${DIFF_WIDTH}px` }} />
              </colgroup>
              <Thead>
                <Tr>
                  <SortHeader label="Date / time" {...sortHeaderProps("created_at")} />
                  <SortHeader label="Actor" {...sortHeaderProps("actor")} />
                  <SortHeader label="Entity" {...sortHeaderProps("entity_type")} />
                  <SortHeader label="Action" {...sortHeaderProps("action")} />
                  <Th>Reason</Th>
                  <Th>Diff</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.audits.map((a) => (
                  <Tr key={a.id} _hover={{ bg: "gray.50" }}>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {fmtDate(a.created_at)}
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {a.actor}
                    </Td>
                    <Td
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      title={`${entityLabel(a.entity_type)} · ${a.entity_id}`}
                    >
                      <Tag size="sm" colorScheme="gray" mr={2} flexShrink={0}>
                        {entityLabel(a.entity_type)}
                      </Tag>
                      <Box
                        as="span"
                        fontFamily={a.entity_type === "creator" && a.creator_email ? undefined : "mono"}
                        fontSize={a.entity_type === "creator" && a.creator_email ? undefined : "xs"}
                      >
                        {entityRef(a)}
                      </Box>
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {a.action}
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {a.reason ?? "—"}
                    </Td>
                    <Td
                      fontFamily="mono"
                      fontSize="xs"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      title={diffText(a.diff)}
                    >
                      {diffText(a.diff)}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      ) : null}
    </Box>
  );
}
